import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  InputNumber,
  message,
  Space,
  Popconfirm,
  List,
  Divider,
} from 'antd';
import MobileTable from '../../../Component/MobileTable';
import { PlusOutlined, DeleteOutlined, ShoppingOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Api from '../../../Network';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InfiniteSelect } from '../../../Component/InfiniteSelect';
import { usePermissions } from '../../../contexts/PermissionContext';

interface CustomerProductsProps {
  customer: any;
  onUpdate?: () => void;
}

interface SelectedProduct {
  id: number;
  name: string;
  name_ar?: string;
  name_en?: string;
  price?: number;
  picture?: string;
}

const CustomerProducts: React.FC<CustomerProductsProps> = ({ customer, onUpdate }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const customerProducts = customer?.products || [];

  // Debug: Log selectedProducts whenever it changes
  useEffect(() => {
    console.log('Selected products state updated:', selectedProducts);
    selectedProducts.forEach((product, index) => {
      console.log(`Product ${index}:`, {
        id: product.id,
        name: product.name,
        name_ar: product.name_ar,
        name_en: product.name_en,
        picture: product.picture,
        hasPicture: !!product.picture,
      });
    });
  }, [selectedProducts]);

  const handleAddProduct = () => {
    setSelectedProducts([]);
    form.resetFields();
    form.setFieldValue('products_select', []);
    setIsModalVisible(true);
  };

  const handleProductsChange = async (selectedValues: any[]) => {
    const currentLang = localStorage.getItem('i18nextLng') || 'en';

    // Helper function to fetch product details from API
    const fetchProductDetails = async (productId: number): Promise<any> => {
      try {
        const response = await Api.get(`/api/admin/products/${productId}`);
        if (response?.status && response?.data) {
          return response.data;
        }
        return null;
      } catch (error) {
        console.error(`Failed to fetch product ${productId}:`, error);
        return null;
      }
    };

    // Convert selected values to SelectedProduct array
    const newSelectedProductsPromises = selectedValues.map(async (value) => {
      // When labelInValue is true, value is an object with {value, label, item}
      // The ID is in value.value (the actual selected value)
      const productId = value?.value;
      let product = value?.item || value;

      // Debug: log the raw value structure
      console.log('Raw value from InfiniteSelect:', {
        value,
        valueValue: value?.value,
        valueLabel: value?.label,
        valueItem: value?.item,
        productId,
        product,
      });

      if (!productId) {
        console.error('Product ID is missing from value.value!', { value });
        return null as any; // Will be filtered out
      }

      // If item is missing, fetch product details from API
      if (!value?.item || !value?.item?.picture) {
        const fetchedProduct = await fetchProductDetails(productId);
        if (fetchedProduct) {
          product = fetchedProduct;
        }
      }

      // Extract name - use label as fallback if product data is missing
      let nameAr = product?.name_ar || product?.nameAr || '';
      let nameEn = product?.name_en || product?.nameEn || product?.name || '';

      // If name is still missing, try to extract from label
      if (!nameAr && !nameEn && value?.label) {
        // Label might be a React node or string - extract text if possible
        const labelText = typeof value.label === 'string' ? value.label : String(value.label);
        // Assume Arabic if label contains Arabic characters
        if (/[\u0600-\u06FF]/.test(labelText)) {
          nameAr = labelText;
        } else {
          nameEn = labelText;
        }
      }

      const productName = currentLang === 'ar' ? nameAr || nameEn : nameEn || nameAr;

      // Check if product already exists in selectedProducts to preserve price and picture
      const existingProduct = selectedProducts.find((p) => p.id === productId);

      // Extract picture - check multiple possible locations and normalize path
      const rawPicture =
        product?.picture ||
        product?.image ||
        product?.picture_url ||
        existingProduct?.picture ||
        undefined;
      // Normalize picture path - handle escaped slashes from JSON
      const picture = rawPicture ? String(rawPicture).replace(/\\/g, '/') : undefined;

      // Debug: log product data to see what's available
      console.log('Extracted product data:', {
        productId,
        nameAr,
        nameEn,
        productName,
        rawPicture: product?.picture,
        picture,
        hasPicture: !!picture,
        fullProduct: product,
      });

      return {
        id: productId,
        name: productName || `Product ${productId}`, // Fallback name
        name_ar: nameAr || undefined,
        name_en: nameEn || undefined,
        price: existingProduct?.price, // Preserve existing price if product was already selected
        picture: picture, // Use product picture or preserve existing
      };
    });

    // Wait for all product details to be fetched
    const newSelectedProducts = (await Promise.all(newSelectedProductsPromises)).filter(
      (p): p is SelectedProduct => p !== null && p !== undefined && p.id !== undefined
    );

    console.log('Final selected products:', newSelectedProducts);

    setSelectedProducts(newSelectedProducts);
    // Update form field to sync with Select component - include picture in item
    const updatedSelectValues = selectedValues.map((value) => {
      const productId = value?.value || value?.item?.id || value?.id;
      const fetchedProduct = newSelectedProducts.find((p) => p.id === productId);
      return {
        ...value,
        value: productId,
        label: value?.label || fetchedProduct?.name || `Product ${productId}`,
        item: fetchedProduct
          ? {
              id: fetchedProduct.id,
              name: fetchedProduct.name,
              name_ar: fetchedProduct.name_ar,
              name_en: fetchedProduct.name_en,
              picture: fetchedProduct.picture,
            }
          : value?.item || {
              id: productId,
              name: value?.label || `Product ${productId}`,
            },
      };
    });
    form.setFieldValue('products_select', updatedSelectValues);
  };

  const handleRemoveProduct = (productId: number) => {
    const updatedProducts = selectedProducts.filter((p) => p.id !== productId);
    setSelectedProducts(updatedProducts);

    // Update the Select value to sync with removed product
    const selectValue = updatedProducts.map((p) => ({
      value: p.id,
      label: p.name,
      item: {
        id: p.id,
        name_ar: p.name_ar,
        name_en: p.name_en,
        picture: p.picture,
      },
    }));
    form.setFieldValue('products_select', selectValue);
  };

  const handlePriceChange = (productId: number, price: number | null) => {
    setSelectedProducts(
      selectedProducts.map((p) => (p.id === productId ? { ...p, price: price || undefined } : p))
    );
  };

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: (productId: number) =>
      Api.delete(`/api/admin/customer/${customer.id}/products/${productId}`),
    onSuccess: () => {
      message.success(t('customers.products.productRemoved', 'Product removed successfully'));
      // Invalidate only the customer data to refresh tab content
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
      onUpdate?.();
    },
    onError: (error: any) => {
      message.error(t('common.error', 'Error removing product'));
      console.error('Error removing product:', error);
    },
  });

  const handleDeleteProduct = async (productId: number) => {
    deleteProductMutation.mutate(productId);
  };

  // Bulk add/update products mutation
  const bulkProductsMutation = useMutation({
    mutationFn: (products: SelectedProduct[]) => {
      const productsData = products
        .filter((p) => p.price !== undefined && p.price !== null)
        .map((p) => ({
          id: p.id,
          price: p.price!,
        }));

      return Api.put('/api/admin/customer/products', {
        customer: customer.id,
        products: productsData,
      });
    },
    onSuccess: () => {
      message.success(t('customers.products.productsUpdated', 'Products updated successfully'));
      setIsModalVisible(false);
      setSelectedProducts([]);
      form.resetFields();
      form.setFieldValue('products_select', []);
      // Invalidate only the customer data to refresh tab content
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
      onUpdate?.();
    },
    onError: (error: any) => {
      message.error(t('common.error', 'Error updating products'));
      console.error('Error updating products:', error);
    },
  });

  const handleSubmit = async () => {
    // Validate that all selected products have prices
    const productsWithoutPrice = selectedProducts.filter(
      (p) => p.price === undefined || p.price === null
    );

    if (productsWithoutPrice.length > 0) {
      message.warning(
        t('customers.products.setPriceForAll', 'Please set a price for all selected products')
      );
      return;
    }

    if (selectedProducts.length === 0) {
      message.warning(
        t('customers.products.selectAtLeastOne', 'Please select at least one product')
      );
      return;
    }

    bulkProductsMutation.mutate(selectedProducts);
  };

  const columns = [
    {
      title: t('products.columns.image', 'Image'),
      dataIndex: 'picture',
      key: 'picture',
      render: (image: string) => {
        const imageUrl = image ? `https://driveshield.net/storage/${image}` : '';
        return imageUrl ? (
          <img
            src={imageUrl}
            alt="Product"
            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div
            style={{
              width: 50,
              height: 50,
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              color: 'var(--color-text-tertiary)',
            }}
          >
            {t('products.noImage', 'No Image')}
          </div>
        );
      },
    },
    {
      title: t('products.columns.name', 'Product Name'),
      key: 'name',
      render: (name: any) => {
        const currentLang = localStorage.getItem('i18nextLng') || 'en';
        const displayName = currentLang === 'ar' ? name?.name_ar : name?.name_en || name;
        return <strong>{displayName}</strong>;
      },
    },
    {
      title: t('products.columns.barcode', 'Product Code'),
      dataIndex: 'barcode',
      key: 'barcode',
    },
    {
      title: t('products.columns.price', 'Price'),
      dataIndex: 'pivot',
      key: 'price',
      render: (pivot: any) => (
        <span className="price">
          {pivot?.price
            ? `${pivot.price} ${t('common.SAR', 'ر.س')}`
            : `${t('common.zero', '0.00')} ${t('common.SAR', 'ر.س')}`}
        </span>
      ),
    },
    // {
    //   title: t('products.columns.status', 'Status'),
    //   dataIndex: 'status',
    //   key: 'status',
    //   render: (status: string) => (
    //     <Tag color={status === 'active' ? 'green' : 'red'}>
    //       {status === 'active' ? t('products.status.active', 'Active') : t('products.status.inactive', 'Inactive')}
    //     </Tag>
    //   ),
    // },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      render: (_: any, record: any) => {
        const canRemove =
          hasPermission('delete_customer_products') ||
          hasPermission('customers.remove_product') ||
          hasPermission('customers.update') ||
          hasPermission('products.delete');

        if (!canRemove) {
          return null;
        }

        return (
          <Space>
            <Popconfirm
              title={t(
                'customers.products.confirmRemove',
                'Are you sure you want to remove this product?'
              )}
              onConfirm={() => handleDeleteProduct(record.id)}
              okText={t('common.yes', 'Yes')}
              cancelText={t('common.no', 'No')}
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                {t('common.remove', 'Remove')}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card
        className="products-card"
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              <ShoppingOutlined style={{ marginRight: 8 }} />
              {t('customers.products.title', 'Customer Products')}
            </span>
            {(hasPermission('update_customer_products') ||
              hasPermission('customers.add_product') ||
              hasPermission('customers.update') ||
              hasPermission('products.create')) && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProduct}>
                {t('customers.products.addProduct', 'Add Product')}
              </Button>
            )}
          </div>
        }
      >
        <MobileTable
          columns={columns}
          dataSource={customerProducts}
          rowKey={(record: any, index: any) => `product-${record.id}-${index}`}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title={t('customers.products.addProducts', 'Add Products')}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedProducts([]);
          form.resetFields();
          form.setFieldValue('products_select', []);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsModalVisible(false);
              setSelectedProducts([]);
              form.resetFields();
              form.setFieldValue('products_select', []);
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            loading={bulkProductsMutation.isPending}
            disabled={
              selectedProducts.length === 0 ||
              !(
                hasPermission('update_customer_products') ||
                hasPermission('customers.add_product') ||
                hasPermission('customers.update') ||
                hasPermission('products.create')
              )
            }
          >
            {t('customers.products.updateAll', 'Update All Products')}
          </Button>,
        ]}
        destroyOnClose
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="products_select"
            label={t('customers.products.selectProducts', 'Select Products')}
          >
            <InfiniteSelect
              queryKey="api/admin/products"
              placeholder={t('customers.products.selectProduct', 'Select products')}
              valueField="id"
              searchField="name"
              singleMode={false}
              customerId={customer.id}
              onChange={handleProductsChange}
              labelRender={(option: any) => {
                const product = option.item || option;
                const currentLang = localStorage.getItem('i18nextLng') || 'en';
                const name =
                  currentLang === 'ar' ? product.name_ar : product.name_en || product.name;
                return name || `Product ${product.id}`;
              }}
              style={{ width: '100%' }}
            />
          </Form.Item>

          {selectedProducts.length > 0 && (
            <>
              <Divider />
              <div style={{ marginBottom: 16 }}>
                <strong>
                  {t('customers.products.selectedProducts', 'Selected Products')} (
                  {selectedProducts.length})
                </strong>
              </div>
              <List
                dataSource={selectedProducts}
                renderItem={(product) => (
                  <List.Item
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          flex: 1,
                        }}
                      >
                        {(() => {
                          // Normalize picture path - handle escaped slashes
                          const picturePath = product.picture
                            ? String(product.picture).replace(/\\/g, '/')
                            : null;
                          const hasPicture =
                            picturePath &&
                            picturePath.trim() !== '' &&
                            picturePath !== 'null' &&
                            picturePath !== 'undefined';
                          const imageUrl = hasPicture
                            ? `https://driveshield.net/storage/${picturePath}`
                            : '';

                          // Debug log
                          if (process.env.NODE_ENV === 'development') {
                            console.log('Rendering product image:', {
                              productId: product.id,
                              picture: product.picture,
                              picturePath,
                              hasPicture,
                              imageUrl,
                            });
                          }

                          return imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={product.name || `Product ${product.id}`}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: 'cover',
                                borderRadius: 4,
                              }}
                              onError={(e) => {
                                console.error('Image load error:', {
                                  src: imageUrl,
                                  productId: product.id,
                                  picture: product.picture,
                                });
                                // On error, replace with placeholder
                                const parent = e.currentTarget.parentElement;
                                if (parent) {
                                  e.currentTarget.style.display = 'none';
                                  const placeholder = parent.querySelector(
                                    '.image-placeholder'
                                  ) as HTMLElement;
                                  if (placeholder) {
                                    placeholder.style.display = 'flex';
                                  }
                                }
                              }}
                              onLoad={() => {
                                console.log('Image loaded successfully:', imageUrl);
                              }}
                            />
                          ) : null;
                        })()}
                        <div
                          className="image-placeholder"
                          style={{
                            width: 50,
                            height: 50,
                            backgroundColor: '#f5f5f5',
                            display: (() => {
                              const picturePath = product.picture
                                ? String(product.picture).replace(/\\/g, '/')
                                : null;
                              const hasPicture =
                                picturePath &&
                                picturePath.trim() !== '' &&
                                picturePath !== 'null' &&
                                picturePath !== 'undefined';
                              return hasPicture ? 'none' : 'flex';
                            })(),
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 4,
                            color: 'var(--color-text-tertiary)',
                            fontSize: '12px',
                          }}
                        >
                          {t('products.noImage', 'No Image')}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, marginBottom: 4 }}>
                            {product.name ||
                              product.name_ar ||
                              product.name_en ||
                              (product.id ? `Product ${product.id}` : 'Unknown Product')}
                          </div>
                        </div>
                      </div>
                      <div style={{ width: 200 }}>
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder={t('customers.products.enterPrice', 'Enter price')}
                          value={product.price}
                          onChange={(value) => handlePriceChange(product.id, value)}
                          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(value) => {
                            const parsed = value?.replace(/\$\s?|(,*)/g, '') || '';
                            return parsed === '' ? 0 : Number.parseFloat(parsed);
                          }}
                          addonAfter="ر.س"
                          min={0}
                          precision={2}
                          disabled={
                            !(
                              hasPermission('update_customer_products') ||
                              hasPermission('customers.add_product') ||
                              hasPermission('customers.update') ||
                              hasPermission('products.create')
                            )
                          }
                        />
                      </div>
                      <Button
                        type="text"
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleRemoveProduct(product.id)}
                        size="small"
                      />
                    </div>
                  </List.Item>
                )}
              />
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CustomerProducts;
